import { buildEmployeeAutoSuggestLabel, NotDismissedEmployeeInput } from 'domain/shared/employee';
import {
  Checkbox,
  ClearAfterUnmountWrapper,
  ClearAfterUnmountWrapperCustomized,
  DateInput,
  DropdownInputOnlyBoolean,
  EnumDropdown,
  FormFieldsData,
  NumberInput,
  TextInput,
  useFormContext,
} from 'framework/form';
import { PROJECT_MEMBERSHIP_ROLES } from 'domain/shared/core/constants';
import { AssignmentFormData, SPOF_TOOLTIP } from 'domain/shared/assignment';
import { ProjectTeamMemberDto, TeamSlotDropdown, useProjectTeamService } from 'domain/shared/newProject';
import { useAsyncValue } from 'framework/utils';
import { useEffect, useState } from 'react';
import { Grid2 as Grid } from '@mui/material';

type StaffingAssignmentFormProps = {
  isCreateForm?: boolean;
  isVirtual?: boolean;
};

const StaffingAssignmentForm = ({ isCreateForm = false, isVirtual = false }: StaffingAssignmentFormProps) => {
  const { data, isDirty } = useFormContext();
  const formData = data as FormFieldsData<AssignmentFormData> | undefined;
  const [hasInitialEmployeeId, setHasInitialEmployeeId] = useState(false);
  const { getProjectTeamByProjectId } = useProjectTeamService();

  const { ACCOUNT_MANAGER, ...projectRolesForDropdown } = PROJECT_MEMBERSHIP_ROLES;
  const isStartedAssignment = !!formData?.from && new Date(formData.from) < new Date();

  const teamResponse = useAsyncValue(async () => {
    if (!formData?.target?.id) {
      return null;
    }
    return getProjectTeamByProjectId(formData.target.id);
  }, [formData?.target?.id]);

  const hasRole = teamResponse && teamResponse.expectedStructure.length > 0;

  useEffect(() => {
    if (!isDirty && formData?.employeeId) {
      setHasInitialEmployeeId(true);
    }
  }, [formData, isDirty]);

  return (
    <Grid container spacing={2} size={12} sx={{ backgroundColor: 'background.paper' }}>
      <Grid size={6}>
        <NotDismissedEmployeeInput source="ownerId" label="Owner" nameSource="ownerName" />
      </Grid>
      <Grid size={6}>
        <ClearAfterUnmountWrapper>
          <NotDismissedEmployeeInput
            source="employeeId"
            label="Worker"
            nameSource="employeeName"
            includeInterns
            includeContractors
            disabled={hasInitialEmployeeId}
          />
        </ClearAfterUnmountWrapper>
      </Grid>
      {!isVirtual && (
        <>
          <Grid size={6}>
            <ClearAfterUnmountWrapper>
              <DropdownInputOnlyBoolean placeholder="Bench" source="bench" label="Bench" />
            </ClearAfterUnmountWrapper>
          </Grid>
          <Grid size={6}>
            <ClearAfterUnmountWrapper isVisible={!formData?.bench}>
              <Checkbox source="isSpof" label="SPOF" tooltip={SPOF_TOOLTIP} />
            </ClearAfterUnmountWrapper>
          </Grid>
          <Grid size={6}>
            <ClearAfterUnmountWrapper>
              <Checkbox source="backup" label="Backup" />
            </ClearAfterUnmountWrapper>
          </Grid>
          <Grid size={6}>
            {formData?.backup && (
              <ClearAfterUnmountWrapperCustomized getSources={() => ['backupForEmployeeId', 'backupForEmployeeName']}>
                <NotDismissedEmployeeInput
                  source="backupForEmployeeId"
                  label="Backup For"
                  nameSource="backupForEmployeeName"
                  errorSource="backupFor"
                  mapResults={employee => {
                    const teamMembers = teamResponse && 'members' in teamResponse ? teamResponse.members : [];
                    const existingTeamMember = teamMembers.find(
                      (member: ProjectTeamMemberDto) => member.employeeAlias === employee.alias,
                    );
                    if (!existingTeamMember) {
                      return null;
                    }

                    return {
                      id: employee.alias,
                      text: buildEmployeeAutoSuggestLabel(employee),
                    };
                  }}
                  includeInterns
                  includeContractors
                />
              </ClearAfterUnmountWrapperCustomized>
            )}
          </Grid>
        </>
      )}
      <Grid size={6}>
        <DateInput source="from" disabled={!isCreateForm && isStartedAssignment} />
      </Grid>
      <Grid size={6}>
        <DateInput source="to" />
      </Grid>
      <Grid size={6}>
        <NumberInput
          source="minPercent"
          integerOnly
          slotProps={{
            htmlInput: {
              min: 0,
              step: 25,
            },
          }}
        />
      </Grid>
      <Grid size={6}>
        <NumberInput
          source="maxPercent"
          integerOnly
          slotProps={{
            htmlInput: {
              min: 0,
              step: 25,
            },
          }}
        />
      </Grid>
      {!isCreateForm && (
        <>
          <Grid size={6}>
            <ClearAfterUnmountWrapper>
              <EnumDropdown options={projectRolesForDropdown} source="membershipRole" disabled={!!hasRole} />
            </ClearAfterUnmountWrapper>
          </Grid>
          <Grid size={6}>
            <ClearAfterUnmountWrapper>
              <TeamSlotDropdown
                disabled={formData?.backup}
                source="slotId"
                projectIdSource="target.id"
                label="Slot"
                team={teamResponse ?? undefined}
              />
            </ClearAfterUnmountWrapper>
          </Grid>
        </>
      )}
      <Grid size={12}>
        <TextInput source="noteText" label="Note" />
      </Grid>
    </Grid>
  );
};

export default StaffingAssignmentForm;
